import axios from "axios";
import { auth } from "../config/firebase";

// This service handles real cancellations and data exports, so a preview or
// staging build that is missing the env var must not quietly transact against
// production. The fallback is kept only for the production origin itself, where
// it is the correct value anyway and where removing it outright would take the
// portal down if the deploy environment ever lost the variable.
const PRODUCTION_SOLUTIONS_API = "https://us-west1-pdx-msa.cloudfunctions.net/solutions";

const resolveSolutionsApi = () => {
  const configured = import.meta.env.VITE_SOLUTIONS_API;
  if (configured) return configured;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (host === "muslimplus.org" || host === "www.muslimplus.org") {
    return PRODUCTION_SOLUTIONS_API;
  }

  // Anywhere else, fail loudly rather than reaching into production.
  console.error(
    "VITE_SOLUTIONS_API is not set. Muslim+ Solutions requests are disabled in this build."
  );
  return "";
};

const SOLUTIONS_API = resolveSolutionsApi();

export const SOLUTIONS_API_BASE = SOLUTIONS_API;

// Authenticated instance for the request form and customer portal
export const solutionsApi = axios.create({
  baseURL: SOLUTIONS_API,
  timeout: 30000,
});

const decodeClaims = (token) => {
  try {
    return JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
  } catch {
    return null;
  }
};

// Add auth token to requests
solutionsApi.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      let token = await user.getIdToken(false);

      // Firebase does not reissue an ID token when the user verifies their
      // email, so the cached token can still carry email_verified:false for up
      // to an hour afterwards. Every Solutions endpoint requires that claim to
      // be true, so without this a customer who just verified is told they have
      // no project. Only refresh on a real mismatch, to avoid a network round
      // trip on every request.
      if (user.emailVerified) {
        const claims = decodeClaims(token);
        if (claims && claims.email_verified !== true) {
          token = await user.getIdToken(true);
        }
      }

      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }
  return config;
});

// Refresh token and retry once on 401
solutionsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return solutionsApi(originalRequest);
        } catch (refreshError) {
          console.error("Error refreshing auth token:", refreshError);
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Project request form (requires a signed-in Muslim+ account; the backend
// takes the requester email from the Firebase token)
// ============================================================================

/**
 * Submit a new Solutions project request
 * @param {object} data - {businessName, contactName, phone, website, projectType, budget, description, agreedToTerms}
 */
export const submitSolutionsRequest = async (data) => {
  const response = await solutionsApi.post("/v1/solutions/requests", data);
  return response.data;
};

// ============================================================================
// Portal: authenticated customer endpoints
// ============================================================================

/**
 * Get the current user's Solutions client record
 */
export const getSolutionsPortalMe = async () => {
  const response = await solutionsApi.get("/v1/solutions/portal/me");
  return response.data;
};

/**
 * Whether the signed-in account has a Muslim+ Solutions project.
 *
 * Solutions membership is deliberately NOT an app role. It is derived from a
 * solutionsClients record matched on the verified email, so a Solutions
 * customer can also be a community member, masjid admin, or halal owner
 * without the two systems interfering.
 *
 * Returns false rather than throwing, so a failed check can never lock
 * somebody out of a signup flow.
 */
export const hasSolutionsProject = async () => {
  try {
    const response = await solutionsApi.get("/v1/solutions/portal/me");
    return response.data?.ok === true && !!response.data?.client;
  } catch {
    return false;
  }
};

/**
 * Create a billing management session, returns {ok, url}
 */
export const openSolutionsBilling = async () => {
  const response = await solutionsApi.post("/v1/solutions/portal/billing");
  return response.data;
};

/**
 * Billing history: past invoices and the card on file.
 * Returns {ok, invoices: [...], paymentMethod: {brand, last4, expMonth, expYear}}
 */
export const getSolutionsInvoices = async () => {
  const response = await solutionsApi.get("/v1/solutions/portal/invoices");
  return response.data;
};

/**
 * Cancel the monthly subscription at the end of the paid period.
 * Self service, no fee, no retention step. Returns {ok, endsAt}
 * @param {string} reason - optional, stored on the Stripe subscription
 */
export const cancelSolutionsSubscription = async (reason = "") => {
  const response = await solutionsApi.post("/v1/solutions/portal/cancel", { reason });
  return response.data;
};

/**
 * Undo a pending cancellation while the paid period is still running
 */
export const reactivateSolutionsSubscription = async () => {
  const response = await solutionsApi.post("/v1/solutions/portal/reactivate");
  return response.data;
};

/**
 * Download everything we hold on this project as a JSON file: the project
 * record, the agreement as it was accepted, every change request, and every
 * enquiry the site collected. Saves straight to disk rather than returning
 * data, since the whole point is that the customer keeps a copy.
 */
export const downloadSolutionsExport = async () => {
  const response = await solutionsApi.get("/v1/solutions/portal/export", {
    responseType: "blob",
  });
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = match?.[1] || "solutions-export.json";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * List change requests for the current client
 */
export const getSolutionsChangeRequests = async () => {
  const rows = new Map(), cursors = new Set();
  let cursor;
  do {
    const response = await solutionsApi.get("/v1/solutions/portal/change-requests", { params: cursor ? { cursor } : {} });
    for (const row of response.data.changeRequests) rows.set(row.id, row);
    cursor = response.data.nextCursor;
    if (cursor && cursors.has(cursor)) throw new Error("Could not load the complete request history. Please refresh.");
    cursors.add(cursor);
  } while (cursor);
  return { ok: true, changeRequests: [...rows.values()] };
};

/**
 * Create a new change request
 * @param {object} data - {title, priority, description}
 */
export const createSolutionsChangeRequest = async (data) => {
  const response = await solutionsApi.post(
    "/v1/solutions/portal/change-requests",
    data
  );
  return response.data;
};
